import { useCallback } from 'react';
import { useNotification } from '@/common/components/notification/NotificationContext';
import {
  useTransferManagerContext,
  TransferJobStatus,
} from '@/context/TransferManagerContext';
import type { ProjectType, ProjectFileType } from '@/generated/graphql';
import { reportTransferError } from '@/services/transferDiagnostics';

interface UseProjectOperationsProps {
  project:
    | ProjectType
    | null
    | undefined
    | { _id: string; name?: string }
    | null;
  latestVersionFiles: ProjectFileType[];
}

/**
 * Hook to manage project operations like delete and download
 * Encapsulates business logic for project-level operations
 */
export function useProjectOperations({
  project,
  latestVersionFiles,
}: UseProjectOperationsProps) {
  const { showError } = useNotification();
  const { enqueueDownload, activeJob } = useTransferManagerContext();

  const handleDownloadProject = useCallback(async () => {
    if (!project) return;

    try {
      // Let user select a download directory
      const dirHandle = await window.showDirectoryPicker({
        mode: 'readwrite',
      });

      const downloadRequests = latestVersionFiles.map(
        (file: ProjectFileType) => ({
          fileId: file._id,
          fileName: file.fileName,
          size: file.size,
          fullHash: file.fullHash,
        })
      );

      await enqueueDownload({
        projectId: project._id,
        projectName: project.name || 'Project',
        files: downloadRequests,
        dirHandle,
        projectFolderName: project.name || 'Project',
      });
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        // User cancelled the directory picker
        return;
      }
      showError('Failed to download project. Please try again.');
      reportTransferError('Prepare project download', error, {
        projectId: project._id,
        projectName: project.name,
        files: latestVersionFiles,
      });
    }
  }, [project, latestVersionFiles, enqueueDownload, showError]);

  return {
    handleDownloadProject,
    isDownloading: activeJob?.status === TransferJobStatus.ACTIVE,
  };
}
